import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@core/components/base-component';
import { StructureDepthEnum } from '@enums/StructureDepth';
import { DepartmentRow } from '@estimate/toolbar/department-modal/models/department.row';
import { Department } from '@interfaces/Department';
import { DepartmentEstimate } from '@interfaces/DepartmentEstimate';
import { EstimateLine } from '@interfaces/EstimateLine';
import { Level1 } from '@interfaces/Level1';
import { Level2 } from '@interfaces/Level2';
import { Structure } from '@interfaces/Structure';
import { EstimateStructureLinksService } from '@shared/services/estimate-structure-links.service';
import { EstimateService } from '@shared/services/estimate.service';
import { LoadingService } from '@shared/services/loading.service';
import { TreeNode } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { Association } from '../models/association.model';
import { LevelType } from '../models/level-type';

@Component({
  templateUrl: './detailed-association-dialog.component.html',
  styleUrls: ['./detailed-association-dialog.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class DetailedAssociationDialogComponent extends BaseComponent implements OnInit {
  private departmentEstimate?: DepartmentEstimate;
  public expandedLevel1Ids: string[] = [];
  public expandedLevel2Ids: string[] = [];

  private inactiveNode = false;
  public level1Tree: TreeNode<Association>[] = [];
  public selectedDepartment: Department;
  private structures: Structure[] = [];
  public isLevel2: boolean;

  public constructor(
    public dialogRef: MatDialogRef<DetailedAssociationDialogComponent>,
    private estimateService: EstimateService,
    private loadingService: LoadingService,
    private estimateStructureLinksService: EstimateStructureLinksService,
    @Inject(MAT_DIALOG_DATA)
    private data: {
      row: DepartmentRow;
    }
  ) {
    super();
  }

  public async ngOnInit() {
    this.selectedDepartment = this.data.row.department;
    this.isLevel2 = this.estimateService.currentEstimate.structureDepth === StructureDepthEnum.Level2;

    await this.loadingService.run(async () => {
      await this.initialiseTree();
    });
  }

  private async initialiseTree() {
    const estimateId = this.estimateService.currentEstimate.id;
    this.departmentEstimate = await firstValueFrom(
      this.estimateService.findDepartmentEstimate(estimateId, this.selectedDepartment.id)
    );
    this.structures = await firstValueFrom(this.estimateService.getStructuresByEstimateId(estimateId));

    this.level1Tree = this.createTree(this.structures);
    await this.updateIsAssoicated();
  }
  private async updateIsAssoicated() {
    const isAssociated = this.level1Tree.every(node => node.data.isAssociated);
    if (isAssociated !== this.departmentEstimate.isAssociatedToAllStructures) {
      this.departmentEstimate.isAssociatedToAllStructures = isAssociated;
      await this.estimateService.saveChangesAsync();
    }
  }

  private createTree(structures: Structure[]) {
    const tree: TreeNode<Association>[] = [];
    for (const struct of structures) {
      if (struct.level1) {
        const level1Node = tree.find(n => n.data.level.code === struct.level1.code);

        if (level1Node) {
          this.addLevel1Node(struct, level1Node);
        } else {
          const node = this.addLevel1Node(struct);
          tree.push(node);
        }
      }
    }

    // expand nodes by default
    for (const node of tree) {
      this.expandNode(node);
      for (const child of node.children) {
        if (child.type === LevelType.Level2) {
          this.expandNode(child);
        }
      }
    }
    return tree;
  }

  private addLevel1Node(structure: Structure, level1Node?: TreeNode<Association>): TreeNode<Association> {
    const isAssociated = this.isAssociated(structure);
    const newNode =
      level1Node ??
      this.createNode(
        {
          level: structure.level1,
          isAssociated: isAssociated
        },
        LevelType.Level1
      );

    // add Departments or Level2s to Level1
    if (this.isLevel2) {
      if (structure.level2) {
        this.addLevel2Node(newNode, structure);
      }
      newNode.data.isAssociated = this.isAssociatedLevel1(newNode);
    }

    return newNode;
  }

  private addLevel2Node(parentNode: TreeNode<Association>, structure: Structure) {
    let level2Node: TreeNode<Association> = null;

    if (parentNode.children.find(n => n.data.level.code === structure.level2.code)) {
      return; // dont re make the same node
    }
    const isAssociated = this.isAssociated(structure);

    // create level2 node
    level2Node = this.createNode(
      {
        level: structure.level2,
        isAssociated: isAssociated
      },
      LevelType.Level2,
      parentNode
    );
    // add to level1 node
    parentNode.children.push(level2Node);
  }

  private createNode(
    association: Association,
    levelType: LevelType,
    parentNode?: TreeNode<Association>
  ): TreeNode<Association> {
    const code = association.level.code ?? '';
    const name = association.level.description ?? '';

    const treeNode: TreeNode<Association> = {
      data: association,
      label: code + ' \u2022 ' + name,
      children: [],
      parent: parentNode,
      type: levelType
    };

    return treeNode;
  }

  public expandNode(node: TreeNode<Association>): void {
    if (node.type === LevelType.Level1) {
      this.expandedLevel1Ids.push(node.data.level.id);
    } else if (node.type === LevelType.Level2) {
      this.expandedLevel2Ids.push(node.data.level.id);
    }
  }

  public collapseNode(node: TreeNode<Association>): void {
    if (node.type === LevelType.Level1) {
      const indexOf = this.expandedLevel1Ids.indexOf(node.data.level.id);
      if (indexOf !== -1) this.expandedLevel1Ids.splice(indexOf, 1);
    } else if (node.type === LevelType.Level2) {
      const indexOf = this.expandedLevel2Ids.indexOf(node.data.level.id);
      if (indexOf !== -1) this.expandedLevel2Ids.splice(indexOf, 1);
    }
  }

  private async link(level: Level1 | Level2) {
    const promises = level.structures.map(structure => {
      const isAlreadyAssociated = this.isAssociated(structure);
      if (isAlreadyAssociated) {
        return;
      }
      return this.estimateStructureLinksService.createEstimateLineBasedOnStructure(
        structure,
        this.estimateService.currentEstimate,
        this.departmentEstimate.id
      );
    });

    await Promise.all(promises);

    await this.initialiseTree();
  }
  private isAssociated(structure: Structure) {
    return !!this.departmentEstimate.estimateLines.find(el => el.structureId === structure.id);
  }

  private isAssociatedLevel1(level1Node?: TreeNode<Association>): boolean {
    return level1Node.children.every(c => c.data.isAssociated);
  }

  private async unlink(level: Level1 | Level2) {
    const detachedEstimateLines: EstimateLine[] = [];

    const promises = level.structures.map(async structure => {
      const estimateLine = this.departmentEstimate.estimateLines.find(el => el.structureId === structure.id);
      if (!estimateLine) {
        return;
      }
      detachedEstimateLines.push(estimateLine);
      await this.estimateService.deleteEstimateLinesByStructByDeptEstim(structure.id, this.departmentEstimate.id);
    });

    this.departmentEstimate.isAssociatedToAllStructures = false;

    await Promise.all(promises);
    await this.estimateService.detachEntities(detachedEstimateLines);
    await this.estimateService.saveChangesAsync();
    await this.initialiseTree();
  }

  public async onAssociationChange(event: Event, node: TreeNode<Association>) {
    const input = event.target as HTMLInputElement;
    await this.loadingService.run(async () => {
      if (input.checked) {
        await this.link(node.data.level);
      } else {
        await this.unlink(node.data.level);
      }
    });
  }

  public onNoClick(): void {
    this.dialogRef.close(this.inactiveNode);
  }
}
